import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Tiny click-outside menu. We will swap to Radix Popover when we add a wider
 * surface (date pickers, command palette). For the user menu this is enough.
 */
export interface DropdownProps {
  trigger: React.ReactNode
  children: React.ReactNode
  align?: 'start' | 'end'
  className?: string
}

export function Dropdown({ trigger, children, align = 'end', className }: DropdownProps) {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-navy-900 hover:bg-navy-50 active:translate-y-px transition-all duration-fluid ease-fluid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {trigger}
      </button>
      {open && (
        <div
          role="menu"
          className={cn(
            'absolute z-30 mt-1 min-w-[14rem] rounded-md border border-slate-200 bg-popover p-1 text-sm shadow-lift animate-fade-in',
            align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          {children}
        </div>
      )}
    </div>
  )
}

export function DropdownItem({
  children, onSelect, destructive, icon,
}: {
  children: React.ReactNode
  onSelect?: () => void
  destructive?: boolean
  icon?: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm',
        'transition-colors duration-fluid ease-fluid',
        destructive
          ? 'text-negative hover:bg-negative-soft'
          : 'text-navy-900 hover:bg-navy-50',
      )}
    >
      {icon && <span className="text-navy-500">{icon}</span>}
      {children}
    </button>
  )
}

export function DropdownLabel({ children }: { children: React.ReactNode }) {
  return <div className="px-2 py-1 text-2xs uppercase tracking-wider text-muted-foreground">{children}</div>
}

export function DropdownSeparator() {
  return <div className="my-1 h-px bg-slate-200" />
}
